/**
 * Accettazione di un invito utente (pagina pubblica, nessuna sessione).
 *
 * Il token arriva nel frammento `#token=` (mai inviato al server nei log di
 * accesso): la pagina lo legge, lo toglie dalla barra degli indirizzi e lo
 * manda nel corpo di anteprima e accettazione. L'invitato compila gli stessi
 * dati anagrafici del form utente (nome, cognome, cellulare, password; l'email
 * è quella dell'invito); ruolo e perimetro sono quelli scelti dall'admin.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircle, Loader2, UserPlus, XCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import apiClient from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';
import type { UserInvitationAcceptInput, UserInvitationAcceptResult, UserInvitationPreview } from '@easyflow/shared';

type Phase = 'loading' | 'form' | 'done' | 'error';

function unwrap<T>(raw: unknown): T {
  const r = raw as { data?: { data?: T } & T };
  return (r.data?.data ?? r.data) as T;
}

/** Stesse regole della password policy del backend (e del form utente). */
const PASSWORD_RULES: Array<{ label: string; test: (p: string) => boolean }> = [
  { label: 'Almeno 8 caratteri', test: (p) => p.length >= 8 },
  { label: 'Almeno una maiuscola (A-Z)', test: (p) => /[A-Z]/.test(p) },
  { label: 'Almeno una minuscola (a-z)', test: (p) => /[a-z]/.test(p) },
  { label: 'Almeno un numero (0-9)', test: (p) => /[0-9]/.test(p) },
  { label: 'Almeno un carattere speciale', test: (p) => /[^A-Za-z0-9]/.test(p) },
];

function readTokenFromHash(): string {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  return params.get('token') ?? '';
}

export default function AcceptInvite() {
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const [token] = useState(readTokenFromHash);
  const [phase, setPhase] = useState<Phase>('loading');
  const [message, setMessage] = useState('');
  const [preview, setPreview] = useState<UserInvitationPreview | null>(null);
  const [resultSlug, setResultSlug] = useState<string | null>(null);
  const [form, setForm] = useState({ first_name: '', last_name: '', mobile: '', password: '', confirm: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Il token non resta nella barra degli indirizzi né nella cronologia.
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    if (!/^[a-f0-9]{64}$/.test(token)) {
      setPhase('error');
      setMessage('Link di invito non valido o incompleto.');
      return;
    }
    apiClient.post('/auth/invitations/preview', { token })
      .then((response) => {
        setPreview(unwrap<UserInvitationPreview>(response));
        setPhase('form');
      })
      .catch((error: unknown) => {
        setPhase('error');
        setMessage(getErrorMessage(error));
      });
  }, [token]);

  const passwordOk = useMemo(() => PASSWORD_RULES.every((r) => r.test(form.password)), [form.password]);
  const canSubmit = !!form.first_name.trim() && !!form.last_name.trim() && passwordOk
    && form.password === form.confirm && !submitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const body: UserInvitationAcceptInput = {
        token,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        password: form.password,
        ...(form.mobile.trim() ? { mobile: form.mobile.trim() } : {}),
      };
      const response = await apiClient.post('/auth/invitations/accept', body);
      setResultSlug(unwrap<UserInvitationAcceptResult>(response)?.tenant_slug ?? null);
      setPhase('done');
    } catch (error) {
      setMessage(getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const goToLogin = () => {
    const slug = resultSlug || tenantSlug;
    navigate(slug ? `/${slug}/login` : '/');
  };

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 rounded-full flex items-center justify-center bg-primary/10">
            {phase === 'loading' && <Loader2 className="h-8 w-8 text-primary animate-spin" />}
            {phase === 'form' && <UserPlus className="h-8 w-8 text-primary" />}
            {phase === 'done' && <CheckCircle className="h-8 w-8 text-success" />}
            {phase === 'error' && <XCircle className="h-8 w-8 text-destructive" />}
          </div>
          <CardTitle className="text-2xl">
            {phase === 'loading' && 'Verifica dell\'invito...'}
            {phase === 'form' && `Benvenuto in ${preview?.tenant_name ?? ''}`}
            {phase === 'done' && 'Account attivato'}
            {phase === 'error' && 'Invito non utilizzabile'}
          </CardTitle>
          <CardDescription>
            {phase === 'form' && preview && (
              <>Completa i tuoi dati per attivare l'account <strong>{preview.email}</strong>
                {preview.role_name ? ` (ruolo: ${preview.role_name})` : ''}.</>
            )}
            {phase === 'done' && 'Ora puoi accedere con la tua email e la password scelta.'}
            {phase === 'error' && message}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {phase === 'form' && (
            <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="inv-first">Nome *</Label>
                  <Input id="inv-first" value={form.first_name} onChange={set('first_name')} maxLength={100} autoComplete="given-name" required />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="inv-last">Cognome *</Label>
                  <Input id="inv-last" value={form.last_name} onChange={set('last_name')} maxLength={100} autoComplete="family-name" required />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="inv-email">Email (username di accesso)</Label>
                <Input id="inv-email" type="email" value={preview?.email ?? ''} autoComplete="username" readOnly disabled />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="inv-mobile">Cellulare</Label>
                <Input id="inv-mobile" type="tel" placeholder="+39 333 1234567" value={form.mobile} onChange={set('mobile')} maxLength={50} autoComplete="tel" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="inv-pass">Password *</Label>
                <Input id="inv-pass" type="password" value={form.password} onChange={set('password')} autoComplete="new-password" required />
                <ul className="text-xs space-y-0.5">
                  {PASSWORD_RULES.map((rule) => (
                    <li key={rule.label} className={rule.test(form.password) ? 'text-success' : 'text-muted-foreground'}>• {rule.label}</li>
                  ))}
                </ul>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="inv-confirm">Conferma password *</Label>
                <Input id="inv-confirm" type="password" value={form.confirm} onChange={set('confirm')} autoComplete="new-password" required />
                {form.confirm && form.confirm !== form.password && (
                  <p className="text-xs text-destructive">Le password non coincidono.</p>
                )}
              </div>
              {message && <p className="text-sm text-destructive">{message}</p>}
              <Button type="submit" disabled={!canSubmit} className="w-full">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Attiva account'}
              </Button>
            </form>
          )}
          {(phase === 'done' || phase === 'error') && (
            <Button onClick={goToLogin} variant={phase === 'done' ? 'default' : 'outline'} className="w-full">
              Vai al login
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
